"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { relationItemPickerLabel, type RelationFieldMeta } from "./relationImportLookup";

export type CsvImportRelationFixField = RelationFieldMeta & { label?: string };

export type RelationFixApplyScope = "row" | "sameValue";

export type CsvImportRelationFixDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field: CsvImportRelationFixField | null;
  relatedItems: Record<string, unknown>[];
  previewObject: Record<string, unknown>;
  highlightFieldName: string;
  resolutionErrorText: string;
  /** Rows (including current) whose raw CSV cell for this field matches the current row (trimmed). */
  sameValueRowCount: number;
  onApply: (uid: string, scope: RelationFixApplyScope) => void;
};

function JsonRowPreview({
  obj,
  highlightKey,
}: {
  obj: Record<string, unknown>;
  highlightKey: string;
}) {
  const entries = Object.entries(obj);
  return (
    <div className="rounded-md border bg-muted/30 p-3 text-left font-mono text-xs leading-relaxed">
      {entries.length === 0 ? (
        <span className="text-muted-foreground">(empty)</span>
      ) : (
        entries.map(([k, v]) => (
          <div key={k} className="break-all">
            <span className="text-muted-foreground">{JSON.stringify(k)}: </span>
            {k === highlightKey ? (
              <span className="text-destructive font-semibold">{JSON.stringify(v)}</span>
            ) : (
              <span>{JSON.stringify(v)}</span>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export function CsvImportRelationFixDialog({
  open,
  onOpenChange,
  field,
  relatedItems,
  previewObject,
  highlightFieldName,
  resolutionErrorText,
  sameValueRowCount,
  onApply,
}: CsvImportRelationFixDialogProps) {
  const [selectedUid, setSelectedUid] = useState<string>("");
  const [applyScope, setApplyScope] = useState<RelationFixApplyScope>("row");

  useEffect(() => {
    if (open) {
      setSelectedUid("");
      setApplyScope("row");
    }
  }, [open, highlightFieldName]);

  const fieldLabel = field?.label || field?.name || "Relation";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fix relation: {fieldLabel}</DialogTitle>
          <DialogDescription>
            The imported value did not match any row in the related collection. Choose the correct related item below.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2 sm:grid-cols-2">
          <div className="space-y-2 min-w-0">
            <Label className="text-sm font-medium">Row data (JSON)</Label>
            <p className="text-xs text-destructive">{resolutionErrorText}</p>
            <JsonRowPreview obj={previewObject} highlightKey={highlightFieldName} />
          </div>
          <div className="space-y-2 min-w-0">
            <Label htmlFor="relation-fix-select" className="text-sm font-medium">
              Related collection item
            </Label>
            <p className="text-xs text-muted-foreground">
              Select the related item to use. The chosen uid is applied for this import only.
            </p>
            <Select value={selectedUid} onValueChange={setSelectedUid}>
              <SelectTrigger id="relation-fix-select" className="w-full">
                <SelectValue placeholder="Choose an item…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {relatedItems.map((item) => {
                  const uidRaw = item.uid ?? item.id;
                  const uid = uidRaw != null ? String(uidRaw) : "";
                  if (!uid) return null;
                  return (
                    <SelectItem key={uid} value={uid}>
                      {field ? relationItemPickerLabel(item, field) : uid}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <div className="space-y-2 pt-2 border-t border-border">
              <Label className="text-sm font-medium">Apply uid to</Label>
              <div className="flex flex-col gap-2.5">
                <label className="flex items-start gap-2 text-sm cursor-pointer leading-snug">
                  <input
                    type="radio"
                    name="relation-fix-apply-scope"
                    className="mt-0.5 shrink-0"
                    checked={applyScope === "row"}
                    onChange={() => setApplyScope("row")}
                  />
                  <span>This row only</span>
                </label>
                {sameValueRowCount > 1 ? (
                  <label className="flex items-start gap-2 text-sm cursor-pointer leading-snug">
                    <input
                      type="radio"
                      name="relation-fix-apply-scope"
                      className="mt-0.5 shrink-0"
                      checked={applyScope === "sameValue"}
                      onChange={() => setApplyScope("sameValue")}
                    />
                    <span>
                      All {sameValueRowCount} rows with the same imported value in this column
                    </span>
                  </label>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!selectedUid}
            onClick={() => {
              if (selectedUid) onApply(selectedUid, applyScope);
            }}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
